// Phone remote, computer side (ADR 0027): a connection of its own to the game server, apart from the rooms, so
// joining or leaving a race never unpairs the phone. The phone's presses arrive here and go into the input router
// as the 'remote' source, exactly like a gamepad: sessions never know where a control came from.
import type { RemotePeerState, ServerMessage } from '@gruvetto/contracts';
import { defaultServerUrl, retryDelay } from './client.js';
import { accessToken, onAuthChange } from '../data/api.js';
import { state } from '../data/store.js';
import { input, controlLayout, onControlLayout } from '../devices/controls.js';
import { isGameAction } from '../devices/input.js';

/** The computer's resume token, kept for the tab's life only: a reload brings the paired phone back. */
const KEY = 'gv.remote.host';
const store = {
  get: () => { try { return sessionStorage.getItem(KEY); } catch { return null; } },
  set: (v: string | null) => { try { if (v) sessionStorage.setItem(KEY, v); else sessionStorage.removeItem(KEY); } catch { /* private mode */ } },
};

export type RemoteStatus = 'off' | 'connecting' | 'on';
export type RemoteProblem = 'signin' | 'unreachable' | null;

/** Address a phone opens to pair: the public one the server knows, else this page's own. */
export function remoteUrl(publicUrl: string | null, secret?: string | null): string {
  const base = (publicUrl || (typeof location === 'undefined' ? '' : location.origin)).replace(/\/+$/, '');
  return `${base}/remote${secret ? '#' + secret : ''}`;
}

class RemoteHost extends EventTarget {
  status: RemoteStatus = 'off';
  phone: RemotePeerState = 'none';
  /** Code to type and QR key (null once a phone used them), and when they stop working (this clock). */
  code: string | null = null;
  key: string | null = null;
  codeUntil = 0;
  problem: RemoteProblem = null;
  publicUrl: string | null = null;
  private ws: WebSocket | null = null;
  private token: string | null = store.get();
  /** The player wants a pairing: the connection comes back after a drop. */
  private wanted = false;
  private tries = 0;
  private retryT: ReturnType<typeof setTimeout> | undefined;
  /** A new code was asked for while the link was down or opening: asked for as soon as it opens. */
  private fresh = false;

  constructor() {
    super();
    onControlLayout(() => this.sendLayout());
    onAuthChange(s => { if (!s) this.stop(); }); // signing out forgets the pairing, like every other token
  }

  get active() { return this.wanted; }

  /** Opens the pairing, or asks for a new code (`fresh`: the previous one stops working, a new phone replaces the paired one). */
  start(fresh = false) {
    this.wanted = true; this.problem = null;
    if (this.ws?.readyState === 1) { if (fresh || (!this.code && this.phone === 'none')) this.send({ t: 'remoteOpen' }); return; }
    if (fresh) this.fresh = true;
    if (!this.ws) this.connect();
  }
  /** After a page reload: takes the pairing back if this tab had one. */
  restore() { if (this.token && !this.wanted) this.start(); }
  /** Unpairs: the phone is told, the token is forgotten. */
  stop() {
    const was = this.wanted;
    this.wanted = false;
    clearTimeout(this.retryT);
    this.fresh = false;
    this.send({ t: 'remoteClose' });
    this.token = null; store.set(null);
    const ws = this.ws;
    this.ws = null;
    ws?.close();
    this.code = null; this.key = null; this.problem = null;
    this.setPhone('none');
    this.status = 'off';
    if (was) this.emit();
  }

  private connect() {
    clearTimeout(this.retryT);
    this.status = 'connecting'; this.emit();
    let ws: WebSocket;
    try { ws = new WebSocket(defaultServerUrl()); } catch { return this.retry(); }
    this.ws = ws;
    ws.onopen = () => {
      this.tries = 0;
      const p = state.profile;
      // the server reads the account from this hello before it handles the pairing (messages run in order)
      this.send({ t: 'hello', name: p.name, color: p.color, mass: p.mass, ftp: p.ftp, token: accessToken() });
      this.send(this.token ? { t: 'remoteOpen', resume: this.token } : { t: 'remoteOpen' });
      if (this.fresh && this.token) this.send({ t: 'remoteOpen' });
      this.fresh = false;
      this.status = 'on';
      this.sendLayout();
      this.emit();
    };
    ws.onmessage = ev => { if (typeof ev.data === 'string') this.onMessage(JSON.parse(ev.data) as ServerMessage); };
    ws.onerror = () => { /* onclose follows */ };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      input.releaseSource('remote'); // a press in flight never gets its release now
      // the server holds the pairing for a while: the phone is away, not unpaired, until it says otherwise
      if (this.phone === 'connected') this.phone = 'away';
      this.status = 'off';
      if (this.wanted) this.retry();
      this.emit();
    };
  }

  private retry() {
    if (!this.wanted) return;
    this.status = 'connecting';
    if (this.tries >= 4) this.problem = 'unreachable';
    this.retryT = setTimeout(() => { if (this.wanted && !this.ws) this.connect(); }, retryDelay(this.tries++));
  }

  private onMessage(m: ServerMessage) {
    switch (m.t) {
      case 'welcome': this.publicUrl = m.publicUrl; break;
      case 'remoteCode':
        this.code = m.code; this.key = m.key; this.codeUntil = Date.now() + m.ttlMs;
        this.token = m.token; store.set(m.token);
        this.problem = null;
        break;
      case 'remotePeer': this.setPhone(m.phone, !!m.paired); break;
      case 'remoteInput': if (isGameAction(m.a)) input.press('remote', 'phone', m.a, m.d); return; // no redraw per press
      case 'error':
        if (/sign in/i.test(m.msg)) { this.problem = 'signin'; this.wanted = false; this.ws?.close(); }
        break;
      default: return;
    }
    this.emit();
  }

  /** `paired`: a phone just used the code, possibly replacing the one paired before (whose presses are released). */
  private setPhone(s: RemotePeerState, paired = false) {
    if (s !== 'connected' || paired) input.releaseSource('remote');
    const lost = s === 'none' && this.phone !== 'none';
    this.phone = s;
    if (paired) this.dispatchEvent(new Event('paired'));
    if (lost) this.dispatchEvent(new Event('unpaired')); // the phone unpaired itself, or the server ended the pairing
  }

  private sendLayout() { this.send({ t: 'remoteState', layout: controlLayout() }); }
  private send(m: object) { if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(m)); }
  private emit() { this.dispatchEvent(new Event('change')); }
}

export const remote = new RemoteHost();
// access point for the end-to-end test; guarded for the Node test environment
if (typeof window !== 'undefined') (window as unknown as { __gvRemote: RemoteHost }).__gvRemote = remote;
